/* eslint-disable no-unused-vars */
import msgpack5 from 'msgpack5'

const msgpack = msgpack5()

export const ReportComponentType = {
  Section: 'section',
  Chart: 'chart',
  Subsection: 'subsection',
}

export interface IReportBlueprint {
  id: string
  name: string
  shortDescription: string
  internalNotes: string
  version: string
  children: IReportComponentBlueprint[]
  components?: IReportComponentBlueprint[]
  lastModified: string
}

export interface IReportComponentBlueprint {
  id: string
  blueprintId: string
  parentId: string
  name: string
  componentType: string | number
  order: number
  children: IReportComponentBlueprint[]
  filters: string[]
  drillDownInfo: IDrillDownBlueprintInfo
  selected?: boolean
  colour?: string
  permitAdd?: boolean
  objectAdd?: {
    title: string
    type: string
    droppable: boolean
  }
  type: string
  columns?: any[]
  rows?: any[]
  deletable?: boolean
}

export interface IDrillDownBlueprintInfo {
  type: number
  linkages: {
    [key: string]: {
      chartName: string
      additionalFilters?: {
        name: string
        value: string
      }[]
    }
  }
}

export interface IReportBlueprintChart {
  id: string
  blueprintId: string
  name: string
  type: string
  componentType: string
  chart: {
    icon: string
  }
  draggable: boolean
  order: number
  children: IReportBlueprintChart[]
  drillDownInfo: {
    linkages: {
      [key: string]: {
        chartName: string
        additionalFilters?: {
          name: string
          value: string
        }[]
      }
    }
    type: number
  }
  columns: any[]
  rows: any[]
  filters: any[]
  header: string
  subHeader: string
  columnHierarchy: any[]
  caption: string
  axisX: any
  axisY: any
  properties: any[]
  chartType: string
  deleted?: boolean
}

export function encodeBlueprint(
  reportBlueprint: IReportBlueprint,
): ReportBlueprintArrayFromBuffer {
  return [
    reportBlueprint.id,
    reportBlueprint.name,
    reportBlueprint.version,
    reportBlueprint.children.map(encodeComponentBlueprint),
    reportBlueprint.lastModified,
  ]
}

function encodeComponentBlueprint(
  component: IReportComponentBlueprint,
): ReportComponentBlueprintFromBuffer {
  const children = component.componentType === 2 ? [] : component.children
  const map = new Map()
  map.set(0, 'chartName')
  return [
    component.id,
    component.blueprintId, // Guid as string
    component.name,
    component.componentType, // int
    component.order,
    children.map(encodeComponentBlueprint), // List of ReportSectionBlueprint instances
    component.filters ?? [], // Array of strings
    [0, map], // DrillDownBlueprintInfo
  ]
}

/**
 * Is going to be removed
 * @param drillDownInfo
 */
function encodeDrillDownInfo(
  drillDownInfo: IDrillDownBlueprintInfo,
): DrillDownBlueprintInfoFromBuffer {
  return [drillDownInfo.type, drillDownInfo.linkages as any]
}

export interface IFilter {
  color: string
  id: string
  name: string
  options: IFilterOption[]
}
export interface IFilterOption {
  text: string
  shortText: string
  value: string
  default: boolean
}
type FilterFromBuffer = [
  filterId: string,
  filterValues: {
    [key: string]: {
      LongName: string
      ShortName: string
    }
  },
]

export const filterColours: { [key: string]: string } = {
  BmGroup: '#FFD700',
  MultiYear: 'orange',
  Period: 'orange',
  PerfFees: 'aquamarine',
  TimePeriod: 'lightGreen',
}

export function decodeFilters(data: Buffer) {
  const decodedData: FilterFromBuffer = msgpack.decode(data)

  const parsedData: IFilter[] = []

  Object.entries(decodedData).forEach(([filterId, filterValues]) => {
    const filterObj: IFilter = {
      color: filterColours[filterId] ?? '#FF0000',
      id: filterId,
      name: filterId,
      options: [],
    }

    Object.entries(filterValues).forEach(([id, values], index) => {
      filterObj.options.push({
        text: values.LongName,
        shortText: values.ShortName,
        value: id,
        default: index === 0,
      })
    })

    parsedData.push(filterObj)
  })
  return parsedData
}

/**
 * Data is a buffer
 * In: [id, title, version, children, lastModified]
 * Out: IReportBlueprint
 * @param data
 */
type ReportBlueprintFromBuffer = [
  id: string,
  title: string,
  version: string,
  children: ReportComponentBlueprintFromBuffer[],
  lastModified: string,
]
/**
 * Data is an array
 * In: [id, title, version, children, lastModified]
 * Out: IReportBlueprint
 * @param data
 */
export type ReportBlueprintArrayFromBuffer = [
  id: string,
  name: string,
  version: string,
  components: ReportComponentBlueprintFromBuffer[],
  lastModified: string,
]

/**
 * Data is an array
 * In: [id, blueprintId, title, componentType, order, children, filters, drillDownInfo]
 * Out: IReportComponentBlueprint
 */
export type ReportComponentBlueprintFromBuffer = [
  id: string,
  blueprintId: string,
  title: string,
  componentType: number | string,
  order: number,
  children: ReportComponentBlueprintFromBuffer[],
  filters: string[],
  drillDownInfo: any[],
]

type DrillDownBlueprintInfoFromBuffer = [
  type: number,
  linkages: [[key: string], [chartName: string, additionalFilters?: [][]]][],
]

export const ChartType = {
  Na: 'Na',
  Table: 'Table',
  BoxPlot: 'BoxPlot',
  ScatterPlot: 'ScatterPlot',
  VariWide: 'Variwide',
  Column: 'Column',
  Bar: 'Bar',
  Line: 'Line',
}

type ReportChartCollectionFromBuffer = [
  title: string,
  filters: FilterFromBuffer[],
  chartType: string,
  header: string[],
  subHeader: string[],
  columnHierarchy: unknown[],
  columns: unknown[],
  rows: unknown[],
  caption: string[],
  axisX: string[],
  axisY: string[],
  properties: unknown,
  drillDownInfo: DrillDownBlueprintInfoFromBuffer,
]
export function decodeReportChart(sourceId: string, chartCollection: Buffer) {
  const chartCollectionArray = msgpack.decode(chartCollection)

  return chartCollectionArray.map(
    (
      item: [
        name: string,
        type: string,
        chart: ReportChartCollectionFromBuffer[],
      ],
      itemIdx: number,
    ) => {
      /**
       * item[0] => chart name
       * item[1] => chart type
       * item[2] => chart info
       */
      const chartInfo = item[2][0]
      const [
        name,
        filters,
        chartType,
        header,
        subHeader,
        columnHierarchy,
        columns,
        rows,
        caption,
        axisX,
        axisY,
        properties,
        drillDownInfo,
      ] = chartInfo

      return {
        id: `${sourceId}-${itemIdx}`,
        blueprintId: '',
        title: name,
        type: ReportComponentType.Chart,
        componentType: 2,
        chart: {
          icon: '',
        },
        draggable: true,
        order: itemIdx,
        children: [],
        drillDownInfo,
        columns,
        rows,
        filters,
        header,
        subHeader,
        columnHierarchy,
        caption,
        axisX,
        axisY,
        properties,
        chartType,
      }
    },
  )
}
